'use client';

import { c as _c } from "react/compiler-runtime";
import { cn } from '@core/utils';
import { cva, type VariantProps } from 'class-variance-authority';
import type { HTMLAttributes, ReactElement } from 'react';
import React, { createContext, useContext, useRef } from 'react';
import { useIsomorphicLayoutEffect } from '../../../hooks';
type TabContextType<T extends string = string> = {
  activeValue: T;
  onChange?: (value: T) => void;
  size?: 'sm' | 'lg';
  variant?: 'full' | 'flex';
};
const TabContext = createContext<TabContextType<string> | undefined>(undefined);
const useTabContext = () => {
  const context = useContext(TabContext);
  if (!context) {
    throw new Error("useTabContext must be used within a TabProvider");
  }
  return context as unknown as TabContextType<T>;
};
const tabItemVariants = cva('relative flex shrink-0 items-center justify-center gap-4 whitespace-nowrap bg-transparent outline-none transition-colors focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-Gray-black/50', {
  variants: {
    size: {
      sm: 'h-53 typo-body4',
      lg: 'h-70 typo-body1'
    },
    variant: {
      full: 'flex-1',
      flex: 'px-24'
    },
    active: {
      true: 'font-700 text-Gray-black',
      false: 'font-400 text-Gray-900 hover:text-Gray-600 active:text-Gray-500'
    },
    disabled: {
      true: 'cursor-not-allowed opacity-50',
      false: 'cursor-pointer'
    }
  },
  compoundVariants: [{
    variant: 'full',
    size: 'sm',
    className: 'px-16'
  }, {
    variant: 'full',
    size: 'lg',
    className: 'px-20'
  }],
  defaultVariants: {
    size: 'sm',
    variant: 'full',
    active: false,
    disabled: false
  }
});
interface TabItemProps extends VariantProps<typeof tabItemVariants> {
  value: string;
  icon?: React.ReactNode;
  disabled?: boolean;
  className?: string;
  children: React.ReactNode;
}
function TabItem(t0) {
  const $ = _c(21);
  const {
    value,
    icon,
    disabled: t1,
    className,
    children
  } = t0;
  const disabled = t1 === undefined ? false : t1;
  const {
    activeValue,
    onChange,
    size,
    variant
  } = useTabContext();
  const isActive = activeValue === value;
  let t2;
  if ($[0] !== disabled || $[1] !== onChange || $[2] !== value) {
    t2 = () => {
      if (!disabled) {
        onChange?.(value);
      }
    };
    $[0] = disabled;
    $[1] = onChange;
    $[2] = value;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const handleClick = t2;
  let t3;
  if ($[4] !== className || $[5] !== disabled || $[6] !== isActive || $[7] !== size || $[8] !== variant) {
    t3 = cn(tabItemVariants({
      size,
      variant,
      active: isActive,
      disabled,
      className
    }));
    $[4] = className;
    $[5] = disabled;
    $[6] = isActive;
    $[7] = size;
    $[8] = variant;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  const t4 = isActive ? 0 : -1;
  let t5;
  if ($[10] !== children) {
    t5 = <span className="flex flex-col items-center"><span aria-hidden={true} className="invisible h-0 overflow-hidden font-700">{children}</span><span>{children}</span></span>;
    $[10] = children;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  let t6;
  if ($[12] !== disabled || $[13] !== handleClick || $[14] !== icon || $[15] !== isActive || $[16] !== t3 || $[17] !== t4 || $[18] !== t5 || $[19] !== value) {
    t6 = <button type="button" className={t3} onClick={handleClick} disabled={disabled} aria-selected={isActive} role="tab" tabIndex={t4} data-value={value}>{t5}{icon}</button>;
    $[12] = disabled;
    $[13] = handleClick;
    $[14] = icon;
    $[15] = isActive;
    $[16] = t3;
    $[17] = t4;
    $[18] = t5;
    $[19] = value;
    $[20] = t6;
  } else {
    t6 = $[20];
  }
  return t6;
}
const tabVariants = cva('relative overflow-x-auto overflow-y-hidden border-b border-Gray-300 hide-scrollbar', {
  variants: {
    size: {
      sm: 'h-53',
      lg: 'h-70'
    },
    variant: {
      full: 'flex',
      flex: 'flex'
    }
  },
  defaultVariants: {
    size: 'sm',
    variant: 'full'
  }
});
interface TabProps<T extends string = string> extends Omit<HTMLAttributes<HTMLDivElement>, 'onChange'>, VariantProps<typeof tabVariants> {
  value: T;
  onChange?: (value: T) => void;
  size?: 'sm' | 'lg';
  variant?: 'full' | 'flex';
  children: React.ReactNode;
  className?: string;
}
type TabComponent = {
  <T extends string = string>(props: TabProps<T>): ReactElement;
  Item: <V extends string>(props: Omit<TabItemProps, 'value'> & {
    value: V;
  }) => ReactElement;
};
function TabBase(t0) {
  const $ = _c(42);
  let children;
  let className;
  let onChange;
  let props;
  let t1;
  let t2;
  let value;
  if ($[0] !== t0) {
    ({
      value,
      onChange,
      size: t1,
      variant: t2,
      children,
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = onChange;
    $[4] = props;
    $[5] = t1;
    $[6] = t2;
    $[7] = value;
  } else {
    children = $[1];
    className = $[2];
    onChange = $[3];
    props = $[4];
    t1 = $[5];
    t2 = $[6];
    value = $[7];
  }
  const size = t1 === undefined ? "sm" : t1;
  const variant = t2 === undefined ? "full" : t2;
  const containerRef = useRef(null);
  const indicatorRef = useRef(null);
  const previousValueRef = useRef(value);
  let t3;
  if ($[8] !== onChange) {
    t3 = newValue => {
      onChange?.(newValue as T);
    };
    $[8] = onChange;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  const handleChange = t3;
  let t4;
  if ($[10] !== value) {
    t4 = () => {
      const container = containerRef.current;
      const indicator = indicatorRef.current;
      if (!container || !indicator) {
        return;
      }
      const activeItem = container.querySelector("[role=\"tab\"][aria-selected=\"true\"]");
      const applyIndicatorPosition = shouldAnimate => {
        if (!activeItem) {
          indicator.getAnimations().forEach(_temp);
          indicator.style.width = "0px";
          return;
        }
        const nextTransform = `translateX(${activeItem.offsetLeft}px)`;
        const nextWidth = `${activeItem.offsetWidth}px`;
        if (indicator.style.transform === nextTransform && indicator.style.width === nextWidth) {
          return;
        }
        const computedStyle = getComputedStyle(indicator);
        const fromTransform = computedStyle.transform;
        const fromWidth = computedStyle.width;
        indicator.getAnimations().forEach(_temp2);
        indicator.style.transform = nextTransform;
        indicator.style.width = nextWidth;
        if (shouldAnimate && fromWidth !== "0px" && !window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
          indicator.animate([{
            transform: fromTransform,
            width: fromWidth
          }, {
            transform: nextTransform,
            width: nextWidth
          }], {
            duration: 200,
            easing: "ease-out"
          });
        }
      };
      const shouldAnimate_0 = previousValueRef.current !== value;
      applyIndicatorPosition(shouldAnimate_0);
      if (shouldAnimate_0 && activeItem) {
        const itemStart = activeItem.offsetLeft;
        const itemEnd = itemStart + activeItem.offsetWidth;
        const visibleStart = container.scrollLeft;
        const visibleEnd = visibleStart + container.clientWidth;
        if (itemStart < visibleStart) {
          container.scrollLeft = Math.max(itemStart - 16, 0);
        } else {
          if (itemEnd > visibleEnd) {
            container.scrollLeft = itemEnd - container.clientWidth + 16;
          }
        }
      }
      previousValueRef.current = value;
      const resizeObserver = new ResizeObserver(() => {
        applyIndicatorPosition(false);
      });
      resizeObserver.observe(container);
      if (activeItem) {
        resizeObserver.observe(activeItem);
      }
      return () => {
        resizeObserver.disconnect();
      };
    };
    $[10] = value;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  let t5;
  if ($[12] !== children || $[13] !== size || $[14] !== value || $[15] !== variant) {
    t5 = [value, size, variant, children];
    $[12] = children;
    $[13] = size;
    $[14] = value;
    $[15] = variant;
    $[16] = t5;
  } else {
    t5 = $[16];
  }
  useIsomorphicLayoutEffect(t4, t5);
  let t6;
  if ($[17] !== handleChange) {
    t6 = event => {
      const container_0 = containerRef.current;
      if (!container_0) {
        return;
      }
      const tabs = Array.from(container_0.querySelectorAll("[role=\"tab\"]:not(:disabled)"));
      if (tabs.length === 0) {
        return;
      }
      const currentIndex = tabs.findIndex(_temp3);
      let nextIndex;
      bb72: switch (event.key) {
        case "ArrowRight":
          {
            nextIndex = currentIndex < 0 ? 0 : (currentIndex + 1) % tabs.length;
            break bb72;
          }
        case "ArrowLeft":
          {
            nextIndex = currentIndex < 0 ? tabs.length - 1 : (currentIndex - 1 + tabs.length) % tabs.length;
            break bb72;
          }
        case "Home":
          {
            nextIndex = 0;
            break bb72;
          }
        case "End":
          {
            nextIndex = tabs.length - 1;
            break bb72;
          }
        default:
          {
            return;
          }
      }
      event.preventDefault();
      const nextTab = tabs[nextIndex];
      nextTab.focus();
      const nextValue = nextTab.dataset.value;
      if (nextValue !== undefined) {
        handleChange(nextValue);
      }
    };
    $[17] = handleChange;
    $[18] = t6;
  } else {
    t6 = $[18];
  }
  const handleKeyDown = t6;
  let t7;
  if ($[19] !== handleChange || $[20] !== size || $[21] !== value || $[22] !== variant) {
    t7 = {
      activeValue: value,
      onChange: handleChange,
      size,
      variant
    };
    $[19] = handleChange;
    $[20] = size;
    $[21] = value;
    $[22] = variant;
    $[23] = t7;
  } else {
    t7 = $[23];
  }
  let t8;
  if ($[24] !== className || $[25] !== size || $[26] !== variant) {
    t8 = cn(tabVariants({
      size,
      variant,
      className
    }));
    $[24] = className;
    $[25] = size;
    $[26] = variant;
    $[27] = t8;
  } else {
    t8 = $[27];
  }
  const t9 = size === "sm";
  const t10 = size === "lg";
  let t11;
  if ($[28] !== t10 || $[29] !== t9) {
    t11 = cn("pointer-events-none absolute bottom-0 left-0 z-10 bg-Gray-black", {
      "h-2": t9,
      "h-4": t10
    });
    $[28] = t10;
    $[29] = t9;
    $[30] = t11;
  } else {
    t11 = $[30];
  }
  let t12;
  if ($[31] !== t11) {
    t12 = <span ref={indicatorRef} aria-hidden={true} className={t11} />;
    $[31] = t11;
    $[32] = t12;
  } else {
    t12 = $[32];
  }
  let t13;
  if ($[33] !== children || $[34] !== handleKeyDown || $[35] !== props || $[36] !== t12 || $[37] !== t8) {
    t13 = <div ref={containerRef} className={t8} role="tablist" tabIndex={-1} onKeyDown={handleKeyDown} {...props}>{t12}{children}</div>;
    $[33] = children;
    $[34] = handleKeyDown;
    $[35] = props;
    $[36] = t12;
    $[37] = t8;
    $[38] = t13;
  } else {
    t13 = $[38];
  }
  let t14;
  if ($[39] !== t13 || $[40] !== t7) {
    t14 = <TabContext.Provider value={t7}>{t13}</TabContext.Provider>;
    $[39] = t13;
    $[40] = t7;
    $[41] = t14;
  } else {
    t14 = $[41];
  }
  return t14;
}
function _temp3(tab) {
  return tab === document.activeElement;
}
function _temp2(animation_0) {
  return animation_0.cancel();
}
function _temp(animation) {
  return animation.cancel();
}
export const Tab = TabBase as unknown as TabComponent;
Tab.Item = TabItem;
export type { TabComponent, TabContextType, TabItemProps, TabProps };
export { useTabContext };